import React, { useState, useEffect, useCallback } from "react";
import { Receipt, Plus, Trash2, Lock, CopyPlus, Repeat, Check } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Modal } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import api from "../../lib/api";
import { rp, PeriodPicker, BranchFilter, useBranches } from "../../components/finance";
import { JournalPreviewButton } from "../../components/JournalPreview";

const EMPTY = { branch_id: "", category_key: "", subcategory: "", description: "", amount: "", notes: "", is_recurring: false };

export default function OperatingExpenses() {
  const toast = useToast();
  const branches = useBranches();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [branchId, setBranchId] = useState("");
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [closed, setClosed] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [carrying, setCarrying] = useState(false);

  useEffect(() => { api.get("/finance-lite/expense-categories").then(({ data }) => setCategories(data.categories || [])); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: cl }] = await Promise.all([
        api.get("/finance-lite/expenses", { params: { period, branch_id: branchId || undefined } }),
        api.get("/finance-lite/closings"),
      ]);
      setRows(data.expenses || []); setTotal(data.total || 0);
      setClosed(!!(cl.closings || []).find((c) => c.period === period && c.branch_id == null && c.status === "closed"));
    } catch { toast.error("Gagal memuat biaya"); }
    finally { setLoading(false); }
  }, [period, branchId, toast]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { const [y, m] = period.split("-"); setForm({ ...EMPTY, branch_id: branchId || (branches[0]?.id || "") }); setModal(true); };

  const save = async () => {
    if (!form.branch_id || !form.category_key || !form.description || !form.amount) { toast.error("Lengkapi cabang, kategori, deskripsi, dan jumlah"); return; }
    setSaving(true);
    const [y, m] = period.split("-");
    try {
      await api.post("/finance-lite/expenses", { branch_id: form.branch_id, year: Number(y), month: Number(m), category_key: form.category_key, subcategory: form.subcategory || null, description: form.description, amount: Number(form.amount), notes: form.notes || null, is_recurring: !!form.is_recurring });
      toast.success("Biaya ditambahkan"); setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const carryForward = async () => {
    setCarrying(true);
    try {
      const { data } = await api.post("/finance-lite/expenses/carry-forward", { period, branch_id: branchId || undefined });
      toast.success(data.created > 0 ? `${data.created} biaya berulang disalin dari ${data.from_period} sebagai draft` : "Tidak ada biaya berulang baru untuk disalin");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyalin biaya berulang"); }
    finally { setCarrying(false); }
  };

  const confirmDraft = async (id) => { try { await api.post(`/finance-lite/expenses/${id}/confirm`); toast.success("Biaya dikonfirmasi"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal mengonfirmasi"); } };

  const del = async (id) => { try { await api.delete(`/finance-lite/expenses/${id}`); toast.success("Biaya dihapus"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); } };

  const subs = categories.find((c) => c.key === form.category_key)?.subs || [];

  return (
    <div data-testid="operating-expenses-page">
      <PageHeader title="Biaya Operasional Bulanan" subtitle="Cukup input sekali per bulan. Payroll otomatis dari HR." icon={Receipt}>
        <BranchFilter value={branchId} onChange={setBranchId} />
        <PeriodPicker value={period} onChange={setPeriod} />
        <Button variant="outline" onClick={carryForward} disabled={closed || carrying} data-testid="fl-carry-forward"><CopyPlus size={15} /> {carrying ? "Menyalin…" : "Salin dari Bulan Lalu"}</Button>
        <Button onClick={openAdd} disabled={closed} data-testid="fl-add-expense"><Plus size={15} /> Tambah Biaya</Button>
      </PageHeader>

      {!closed && (
        <Card className="mb-4 flex items-start gap-3 border-primary/20 bg-primary/5 p-3" data-testid="fl-recurring-hint">
          <Repeat size={16} className="mt-0.5 shrink-0 text-primary" />
          <p className="text-sm text-ink-soft">Biaya rutin (listrik, air, gas, internet, sewa, pest control, domain &amp; hosting, langganan software) cukup ditandai <b>Berulang</b> sekali. Klik <b>Salin dari Bulan Lalu</b> untuk menyalin nilainya sebagai <b>draft</b> — Anda tinggal meninjau &amp; menyesuaikan jika perlu.</p>
        </Card>
      )}

      {closed && <Card className="mb-4 flex items-center gap-2 border-warn/40 bg-warn/5 p-3" data-testid="fl-expense-locked"><Lock size={16} className="text-warn" /><span className="text-sm text-ink-soft">Bulan {period} sudah <b>ditutup</b>. Buka kembali di menu Tutup Bulan untuk mengubah biaya.</span></Card>}

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        rows.length === 0 ? (
          <Card className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-line py-12 text-center" data-testid="fl-expense-empty">
            <Receipt size={40} className="mb-3 text-ink-mute" />
            <h3 className="font-heading text-lg font-bold text-ink">Belum ada biaya bulan ini</h3>
            <p className="mt-1 max-w-sm text-sm text-ink-soft">Tambahkan biaya operasional seperti listrik, sewa, atau pemasaran untuk melengkapi estimasi laba rugi.</p>
          </Card>
        ) : (
        <Card className="overflow-hidden p-0" data-testid="fl-expense-table">
          <div className="flex items-center justify-between border-b border-line px-5 py-3"><h3 className="font-heading text-sm font-bold text-ink">Daftar Biaya · {period}</h3><span className="text-sm font-extrabold text-ink" data-testid="fl-expense-total">Total: {rp(total)}</span></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-left text-xs font-bold uppercase tracking-wide text-ink-mute">
                <tr><th className="px-5 py-2">Kategori</th><th className="px-3 py-2">Deskripsi</th><th className="px-3 py-2">Cabang</th><th className="px-3 py-2 text-right">Jumlah</th><th className="px-5 py-2"></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-line/60" data-testid={`fl-expense-row-${r.id}`}>
                    <td className="px-5 py-2">
                      <span className="font-semibold text-ink">{r.category_name}</span>{r.subcategory && <span className="text-ink-mute"> · {r.subcategory}</span>}
                      <span className="ml-2 inline-flex gap-1 align-middle">
                        {r.is_recurring && <Badge tone="neutral" data-testid={`fl-badge-recurring-${r.id}`}><Repeat size={11} /> Berulang</Badge>}
                        {r.status === "draft" && <Badge tone="warn" data-testid={`fl-badge-draft-${r.id}`}>Draft</Badge>}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-ink-soft">{r.description}</td>
                    <td className="px-3 py-2 text-ink-soft">{r.branch_name}</td>
                    <td className="px-3 py-2 text-right font-semibold text-ink">{rp(r.amount)}</td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {r.status !== "draft" && <JournalPreviewButton module="opex" docId={r.id} />}
                        {r.status === "draft" && <button onClick={() => confirmDraft(r.id)} disabled={closed} className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-30" title="Konfirmasi draft" data-testid={`fl-confirm-expense-${r.id}`}><Check size={14} /></button>}
                        <button onClick={() => del(r.id)} disabled={closed} className="rounded-lg p-1.5 text-danger hover:bg-danger-light disabled:opacity-30" data-testid={`fl-del-expense-${r.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        )
      )}

      <Modal open={modal} onClose={() => setModal(false)} title="Tambah Biaya Operasional">
        <div className="space-y-3">
          <div><label className="text-xs font-semibold text-ink-soft">Cabang</label>
            <select className="cf-input mt-1 w-full" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="fl-form-branch">
              <option value="">Pilih cabang</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-ink-soft">Kategori</label>
              <select className="cf-input mt-1 w-full" value={form.category_key} onChange={(e) => setForm({ ...form, category_key: e.target.value, subcategory: "" })} data-testid="fl-form-category">
                <option value="">Pilih kategori</option>{categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
            </div>
            <div><label className="text-xs font-semibold text-ink-soft">Sub-kategori</label>
              <select className="cf-input mt-1 w-full" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="fl-form-subcategory">
                <option value="">(opsional)</option>{subs.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-xs font-semibold text-ink-soft">Deskripsi</label><input className="cf-input mt-1 w-full" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="mis. Tagihan listrik PLN Juli" data-testid="fl-form-description" /></div>
          <div><label className="text-xs font-semibold text-ink-soft">Jumlah (Rp)</label><input type="number" className="cf-input mt-1 w-full" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fl-form-amount" /></div>
          <div><label className="text-xs font-semibold text-ink-soft">Catatan (opsional)</label><input className="cf-input mt-1 w-full" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="fl-form-notes" /></div>
          <label className="flex items-center gap-2 rounded-xl bg-primary/5 p-3 text-sm text-ink-soft"><input type="checkbox" checked={!!form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} data-testid="fl-form-recurring" /> <span><b>Biaya berulang tiap bulan</b> — otomatis dapat disalin ke bulan berikutnya sebagai draft.</span></label>
          <div className="flex justify-end gap-2 pt-2"><Button variant="outline" onClick={() => setModal(false)}>Batal</Button><Button onClick={save} disabled={saving} data-testid="fl-form-save">{saving ? "Menyimpan…" : "Simpan"}</Button></div>
        </div>
      </Modal>
    </div>
  );
}
